import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { signIn, signInWithRedirect, signOut } from 'aws-amplify/auth';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close, ErrorOutline, Visibility, VisibilityOff } from '@mui/icons-material';
import logo from '../../Images/meshLogo.svg';
import { getPublicSsoDiscovery } from '../../services/publicSsoDiscovery';
import * as constants from './constants';
import { getCustomerIdFromSearch, getTenantScopedRoute } from './customerId';
import { getSsoErrorFromSearch } from './ssoError';
import { getPrimaryButtonSx, getSecondaryButtonSx, getTextFieldSx } from './styles';

const getProviderLabel = (providerName = '') => {
  const normalized = (providerName || '').toLowerCase();
  if (normalized.includes('okta')) {
    return 'Okta';
  }
  if (normalized.includes('google')) {
    return 'Google Workspace';
  }
  if (normalized.includes('azure') || normalized.includes('entra')) {
    return 'Azure AD';
  }
  return 'SSO';
};

const getStoredPostLoginRedirect = () =>
  sessionStorage.getItem('postLoginRedirect') || constants.ROUTE_HOME;

const getDefaultIdentityProviders = (providerName = '') => {
  const normalizedProviderName = String(providerName || '').trim();
  if (!normalizedProviderName) {
    return [];
  }

  return [{
    provider: normalizedProviderName,
    idpIdentifier: normalizedProviderName,
    displayName: getProviderLabel(normalizedProviderName),
  }];
};

const normalizeIdentityProviders = (identityProviders = []) => {
  if (!Array.isArray(identityProviders)) {
    return [];
  }

  const seen = new Set();

  return identityProviders.reduce((providers, identityProvider) => {
    const idpIdentifier = String(identityProvider?.idpIdentifier || '').trim();
    if (!idpIdentifier || seen.has(idpIdentifier)) {
      return providers;
    }

    seen.add(idpIdentifier);
    providers.push({
      provider: identityProvider?.provider || '',
      idpIdentifier,
      displayName: String(identityProvider?.displayName || '').trim(),
      logoUrl: identityProvider?.logoUrl || null,
    });
    return providers;
  }, []);
};

const getIdentityProviderLabel = (identityProvider) =>
  identityProvider?.displayName?.trim() ||
  getProviderLabel(identityProvider?.provider || identityProvider?.idpIdentifier || '');

const Login = () => {
  const location = useLocation();
  const theme = useTheme();
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [discoveryErrorMessage, setDiscoveryErrorMessage] = useState('');
  const [loadingAction, setLoadingAction] = useState(null);
  const [discoveryRequestNonce, setDiscoveryRequestNonce] = useState(0);
  const configuredProviderName = process.env.REACT_APP_COGNITO_IDP || '';
  const customerId = getCustomerIdFromSearch(location.search);
  const [identityProviders, setIdentityProviders] = useState(() =>
    customerId ? [] : getDefaultIdentityProviders(configuredProviderName),
  );
  const [passwordLoginEnabled, setPasswordLoginEnabled] = useState(!customerId);
  const [isDiscoveryLoading, setIsDiscoveryLoading] = useState(Boolean(customerId));
  const hasSsoOption = identityProviders.length > 0;
  const hasRedirectedToApp = useRef(false);
  const from = location.state?.from?.pathname || getStoredPostLoginRedirect();

  const redirectToApp = useCallback((destination = from) => {
    sessionStorage.removeItem('postLoginRedirect');
    window.location.href = destination;
  }, [from]);

  useEffect(() => {
    const pathnameFromState = location.state?.from?.pathname;
    if (pathnameFromState && authStatus !== 'authenticated') {
      sessionStorage.setItem('postLoginRedirect', pathnameFromState);
    }
  }, [authStatus, location.state?.from?.pathname]);

  useEffect(() => {
    if (authStatus === 'authenticated' && !hasRedirectedToApp.current) {
      hasRedirectedToApp.current = true;
      redirectToApp();
    }
  }, [authStatus, redirectToApp]);

  useEffect(() => {
    const errorFromQuery = getSsoErrorFromSearch(location.search);
    if (errorFromQuery) {
      setErrorMessage(errorFromQuery);
      sessionStorage.removeItem('ssoError');
      return;
    }

    const storedErrorMessage = sessionStorage.getItem('ssoError');
    if (storedErrorMessage) {
      setErrorMessage(storedErrorMessage);
      sessionStorage.removeItem('ssoError');
      return;
    }

    setErrorMessage('');
  }, [location.search]);

  useEffect(() => {
    const fallbackIdentityProviders = getDefaultIdentityProviders(configuredProviderName);

    if (!customerId) {
      sessionStorage.removeItem(constants.SSO_CUSTOMER_ID_STORAGE_KEY);
      setDiscoveryErrorMessage('');
      setIdentityProviders(fallbackIdentityProviders);
      setPasswordLoginEnabled(true);
      setIsDiscoveryLoading(false);
      return undefined;
    }

    sessionStorage.setItem(constants.SSO_CUSTOMER_ID_STORAGE_KEY, customerId);
    setDiscoveryErrorMessage('');
    setIdentityProviders([]);
    setPasswordLoginEnabled(false);
    setIsDiscoveryLoading(true);

    let isCurrent = true;
    const controller = new AbortController();

    getPublicSsoDiscovery({
      customerId,
      signal: controller.signal,
    })
      .then((discovery) => {
        if (!isCurrent) {
          return;
        }

        setDiscoveryErrorMessage('');
        setIdentityProviders(normalizeIdentityProviders(discovery.identityProviders));
        setPasswordLoginEnabled(discovery.passwordLoginEnabled !== false);
        setIsDiscoveryLoading(false);
      })
      .catch((error) => {
        if (!isCurrent || error.name === 'AbortError') {
          return;
        }

        setDiscoveryErrorMessage(error.message || 'Failed to load SSO options for this customer.');
        setIdentityProviders([]);
        setPasswordLoginEnabled(true);
        setIsDiscoveryLoading(false);
      });

    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [configuredProviderName, customerId, discoveryRequestNonce]);

  const retryDiscovery = useCallback(() => {
    setErrorMessage('');
    setDiscoveryErrorMessage('');
    setDiscoveryRequestNonce((current) => current + 1);
  }, []);

  const handleSsoSignIn = async (idpIdentifier) => {
    if (!idpIdentifier) {
      return;
    }

    setErrorMessage('');
    setDiscoveryErrorMessage('');
    setLoadingAction(`sso:${idpIdentifier}`);
    if (customerId) {
      sessionStorage.setItem(constants.SSO_CUSTOMER_ID_STORAGE_KEY, customerId);
    }

    try {
      await signInWithRedirect({ provider: { custom: idpIdentifier } });
    } catch (err) {
      if (err.name === 'UserAlreadyAuthenticatedException') {
        redirectToApp();
        return;
      }

      setErrorMessage(err.message || constants.DEFAULT_SIGN_IN_ERROR_MESSAGE);
    } finally {
      setLoadingAction(null);
    }
  };

  const handlePasswordSignIn = async (isRetry = false) => {
    setErrorMessage('');
    setDiscoveryErrorMessage('');
    setLoadingAction('password');

    try {
      const { isSignedIn, nextStep } = await signIn({
        username: username.trim(),
        password,
      });

      if (isSignedIn) {
        redirectToApp();
        return;
      }

      setErrorMessage(
        constants.ADDITIONAL_STEP_ERROR_MESSAGE(nextStep?.signInStep || 'UNKNOWN'),
      );
    } catch (err) {
      if (err.name === 'UserAlreadyAuthenticatedException' && !isRetry) {
        try {
          await signOut();
          return handlePasswordSignIn(true);
        } catch (signOutError) {
          setErrorMessage(signOutError.message || constants.DEFAULT_SIGN_IN_ERROR_MESSAGE);
        }
      } else {
        setErrorMessage(err.message || constants.DEFAULT_SIGN_IN_ERROR_MESSAGE);
      }
    }

    setLoadingAction(null);
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    await handlePasswordSignIn();
  };

  const textFieldSx = getTextFieldSx(theme);
  const primaryButtonSx = getPrimaryButtonSx(theme);
  const secondaryButtonSx = getSecondaryButtonSx(theme);

  const displayedErrorMessage = errorMessage || discoveryErrorMessage;
  const showError = Boolean(displayedErrorMessage);
  const canSubmitPassword =
    username.trim().length > 0 && password.length > 0 && loadingAction === null;

  return (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        justifyContent: 'center',
        minHeight: '100vh',
        px: { xs: 2, sm: 5.25 },
        py: { xs: 2, sm: 2.75 },
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: theme.palette.background.paper,
          borderRadius: theme.spacing(2),
          boxShadow: '0px 6px 10px rgba(0, 0, 0, 0.15), 0px 2px 3px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          maxWidth: 555,
          minWidth: { sm: 555 },
          p: 6,
          textAlign: 'center',
          width: '100%',
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Mesh logo"
          sx={{
            height: 31,
            width: 'auto',
          }}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            maxWidth: 459,
            width: '100%',
          }}
        >
          <Typography
            component="h1"
            sx={{
              color: theme.palette.text.primary,
              fontSize: '30px',
              fontWeight: 700,
              lineHeight: '36px',
            }}
          >
            {constants.LOGIN_TITLE}
          </Typography>
          {showError ? (
            <Box
              role="alert"
              sx={{
                alignItems: 'flex-start',
                backgroundColor: '#fbd9d8',
                borderRadius: theme.spacing(0.5),
                boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.3), 0px 1px 3px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                gap: 1,
                px: 1.5,
                py: 1.75,
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', gap: 0.75, flex: 1 }}>
                <ErrorOutline sx={{ color: '#90141c', fontSize: 20, mt: '2px' }} />
                <Typography
                  sx={{
                    color: '#90141c',
                    flex: 1,
                    fontSize: '14px',
                    fontWeight: theme.typography.fontWeightMedium,
                    lineHeight: '20px',
                    textAlign: 'left',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {displayedErrorMessage}
                </Typography>
              </Box>
              <IconButton
                aria-label="Dismiss error"
                onClick={() => {
                  setErrorMessage('');
                  setDiscoveryErrorMessage('');
                }}
                size="small"
                sx={{ color: '#90141c', p: 0.5 }}
              >
                <Close sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          ) : null}
          {isDiscoveryLoading ? (
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '14px',
                lineHeight: '20px',
              }}
            >
              Loading sign-in options...
            </Typography>
          ) : null}
          {customerId && discoveryErrorMessage && !isDiscoveryLoading ? (
            <Button
              disabled={loadingAction !== null}
              onClick={retryDiscovery}
              variant="text"
              sx={{
                alignSelf: 'center',
                color: theme.palette.primary.main,
                fontSize: '14px',
                fontWeight: theme.typography.fontWeightMedium,
                lineHeight: '20px',
                textTransform: 'none',
                width: 'fit-content',
              }}
            >
              Retry loading sign-in options
            </Button>
          ) : null}
          {identityProviders.map((identityProvider) => {
            const isSsoLoading = loadingAction === `sso:${identityProvider.idpIdentifier}`;

            return (
              <Button
                key={identityProvider.idpIdentifier}
                disableElevation
                disabled={loadingAction !== null}
                onClick={() => handleSsoSignIn(identityProvider.idpIdentifier)}
                variant="contained"
                sx={primaryButtonSx}
              >
                {isSsoLoading
                  ? constants.SSO_BUTTON_LOADING
                  : `Sign in with ${getIdentityProviderLabel(identityProvider)}`}
              </Button>
            );
          })}
          {hasSsoOption && passwordLoginEnabled ? (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 1,
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <Box sx={{ borderTop: '1px solid #d6d6d6', flex: 1 }} />
              <Typography
                sx={{
                  color: '#797979',
                  fontSize: '14px',
                  fontWeight: theme.typography.fontWeightRegular,
                  lineHeight: '18px',
                }}
              >
                or
              </Typography>
              <Box sx={{ borderTop: '1px solid #d6d6d6', flex: 1 }} />
            </Box>
          ) : null}
          {passwordLoginEnabled ? (
            <Box component="form" onSubmit={handlePasswordSubmit} sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, width: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography
                  component="label"
                  htmlFor="login-email"
                  sx={{
                    color: theme.palette.text.primary,
                    fontSize: '16px',
                    lineHeight: '24px',
                    textAlign: 'left',
                  }}
                >
                  {constants.LOGIN_EMAIL_LABEL}
                </Typography>
                <TextField
                  autoComplete="username email"
                  disabled={loadingAction !== null}
                  fullWidth
                  id="login-email"
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder={constants.LOGIN_EMAIL_PLACEHOLDER}
                  size="small"
                  sx={textFieldSx}
                  value={username}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography
                  component="label"
                  htmlFor="login-password"
                  sx={{
                    color: theme.palette.text.primary,
                    fontSize: '16px',
                    lineHeight: '24px',
                    textAlign: 'left',
                  }}
                >
                  {constants.LOGIN_PASSWORD_LABEL}
                </Typography>
                <TextField
                  autoComplete="current-password"
                  disabled={loadingAction !== null}
                  fullWidth
                  id="login-password"
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={constants.LOGIN_PASSWORD_PLACEHOLDER}
                  size="small"
                  sx={textFieldSx}
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          edge="end"
                          onClick={() => setShowPassword((previous) => !previous)}
                          onMouseDown={(event) => event.preventDefault()}
                          size="small"
                          sx={{ mr: 0.5 }}
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                justifyContent: 'center',
                mt: 2.5,
              }}
            >
              <Typography
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: '14px',
                  fontWeight: theme.typography.fontWeightRegular,
                  lineHeight: '18px',
                }}
              >
                {constants.TERMS_INTRO}
              </Typography>
              <Link
                href={constants.TERMS_URL}
                target="_blank"
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: '14px',
                  fontWeight: theme.typography.fontWeightMedium,
                  lineHeight: '20px',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {constants.TERMS_LINK_LABEL}
              </Link>
              <Typography
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: '14px',
                  fontWeight: theme.typography.fontWeightRegular,
                  lineHeight: '18px',
                }}
              >
                ,
              </Typography>
              <Link
                href={constants.PRIVACY_POLICY_URL}
                target="_blank"
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: '14px',
                  fontWeight: theme.typography.fontWeightMedium,
                  lineHeight: '20px',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {constants.PRIVACY_POLICY_LINK_LABEL}
              </Link>
              <Typography
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: '14px',
                  fontWeight: theme.typography.fontWeightRegular,
                  lineHeight: '18px',
                }}
              >
                , and
              </Typography>
              <Link
                href={constants.COOKIE_POLICY_URL}
                target="_blank"
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: '14px',
                  fontWeight: theme.typography.fontWeightMedium,
                  lineHeight: '20px',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {constants.COOKIE_LINK_LABEL}
              </Link>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Button
                disabled={!canSubmitPassword}
                type="submit"
                variant={hasSsoOption ? 'outlined' : 'contained'}
                sx={hasSsoOption ? secondaryButtonSx : primaryButtonSx}
              >
                {loadingAction === 'password'
                  ? constants.LOGIN_BUTTON_LOADING
                  : constants.LOGIN_BUTTON_LABEL}
              </Button>
              <Link
                color="primary"
                component={RouterLink}
                state={{ from: location.state?.from }}
                sx={{
                  fontSize: '16px',
                  fontWeight: theme.typography.fontWeightMedium,
                  lineHeight: '24px',
                  textAlign: 'center',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
                to={getTenantScopedRoute(constants.ROUTE_FORGOT_PASSWORD, customerId)}
              >
                {constants.FORGOT_PASSWORD_LABEL}
              </Link>
            </Box>
            </Box>
          ) : null}
        </Box>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              color: '#797979',
              fontSize: '14px',
              fontWeight: theme.typography.fontWeightRegular,
              lineHeight: '18px',
            }}
          >
            {constants.TALK_TO_SALES_PROMPT}
          </Typography>
          <Link
            href={constants.TALK_TO_SALES_URL}
            sx={{
              color: theme.palette.primary.main,
              fontSize: '14px',
              fontWeight: theme.typography.fontWeightMedium,
              lineHeight: '20px',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {constants.TALK_TO_SALES_LABEL}
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
